import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper'
import CustomButton from '@/components/CustomButton'

// Swiper 样式
import 'swiper/swiper-bundle.css'

interface BootItem {
  image: string
  text: string
  description: string
}

const FIRST_VISIT_KEY = 'hasVisitedBoot'

export default function Boot() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const swiperRef = useRef<SwiperType | null>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isTextVisible, setIsTextVisible] = useState(true)

  // 检查登录状态，已登录用户不能访问引导页
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('token')
    if (isLoggedIn) {
      navigate('/home', { replace: true })
    }
  }, [navigate])

  const items: BootItem[] = [
    {
      image: '/images/boot/boot-swiper1.png',
      text: t('全球资产一账尽揽'),
      description: t('全球资产一账尽揽描述')
    },
    {
      image: '/images/boot/boot-swiper2.png',
      text: t('跨境收付双向畅通'),
      description: t('跨境收付双向畅通描述')
    },
    {
      image: '/images/boot/boot-swiper3.png',
      text: t('通用卡片全球自由支付'),
      description: t('通用卡片全球自由支付描述')
    },
    {
      image: '/images/boot/boot-swiper4.png',
      text: t('安全防护让窃贼止步'),
      description: t('安全防护让窃贼止步描述')
    },
    {
      image: '/images/boot/boot-swiper5.png',
      text: t('国际账户全球通用'),
      description: t('国际账户全球通用描述')
    }
  ]

  // 标记用户已访问过引导页
  useEffect(() => {
    localStorage.setItem(FIRST_VISIT_KEY, 'true')
  }, [])

  // 处理跳过按钮
  const handleSkip = useCallback(() => {
    navigate('/login', { replace: true })
  }, [navigate])

  // 处理滑动到最后一页后的再次滑动
  const handleSlideChange = useCallback((swiper: SwiperType) => {
    // 先淡出文字
    setIsTextVisible(false)

    // 延迟更新索引和淡入
    setTimeout(() => {
      setCurrentIndex(swiper.activeIndex)
      setIsTextVisible(true)
    }, 200)
  }, [])

  const handleSlideChangeTransitionEnd = useCallback((swiper: SwiperType) => {
    // 如果在最后一页且尝试继续滑动，跳转到登录页
    if (swiper.isEnd && swiper.activeIndex === items.length - 1) {
      // 这里可以添加一个延迟或者监听用户的滑动手势
    }
  }, [items.length])

  return (
    <div className="bg-[linear-gradient(176.8deg,#9CBBFF_2.64%,#578CFF_75.54%)] h-[100dvh] flex flex-col overflow-hidden animation-fadeIn relative">
      {/* 分页指示器 - 移到顶部 */}
      <div className="flex justify-center space-x-1 py-4 px-6 flex-shrink-0">
        {items.map((_, index) => (
          <div
            key={index}
            className={`w-1/5 h-1.5 rounded-full transition-all duration-300 ${index === currentIndex
              ? 'bg-white'
              : 'bg-white/50'
              }`}
          />
        ))}
      </div>

      {/* 全屏 Swiper 容器 */}
      <div className="flex-1 w-full overflow-hidden">
        <Swiper
          onSwiper={(swiper) => { swiperRef.current = swiper }}
          onSlideChange={handleSlideChange}
          onSlideChangeTransitionEnd={handleSlideChangeTransitionEnd}
          className="h-full w-full"
          spaceBetween={0}
          slidesPerView={1}
        >
          {items.map((item, index) => (
            <SwiperSlide key={index}>
              <div className="h-full relative px-6">
                {/* 文字描述 - 固定在顶部 */}
                <div className="absolute top-0 left-6 right-6 max-w-sm">
                  <div
                    className="text-7 font-bold leading-relaxed transition-opacity duration-300 text-[#131D60] mt-4 mb-2"
                    style={{ opacity: isTextVisible ? 1 : 0 }}
                  >
                    {item.text}
                  </div>
                  <div className='leading-relaxed text-sm transition-opacity duration-300 text-[#131D60]'
                    style={{ opacity: isTextVisible ? 1 : 0 }}>
                    {item.description}
                  </div>
                </div>
                
                {/* 图片 - 整体居中 */}
                {item.image && (
                  <div className="absolute inset-0 z--1 flex items-center justify-center">
                    <img
                      src={item.image}
                      alt={`slide-${index}`}
                      className="max-w-full max-h-[60vh] object-contain"
                    />
                  </div>
                )}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      <div className='absolute bottom-8 w-full z-20 px-6'>
        <CustomButton color='default' onClick={handleSkip}>{t('开始使用')}</CustomButton>
      </div>
    </div>
  )
}